import { useEffect, useRef, useState } from "react";
import { useStore } from "@/lib/store";
import { ContextPanelBody } from "../context/ContextPanel";
import { PromptInspector } from "./PromptInspector";

function formatCtx(n: number | null | undefined): string {
  if (!n) return "";
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return String(n);
}

type Props = { isStreaming?: boolean };

export function RequestBar({ isStreaming = false }: Props) {
  const models = useStore((s) => s.models);
  const activeModel = useStore((s) => s.activeModel);
  const setActiveModel = useStore((s) => s.setActiveModel);
  const refreshModels = useStore((s) => s.refreshModels);
  const thinkingEffort = useStore((s) => s.settings.thinkingEffort);
  const updateSettings = useStore((s) => s.updateSettings);
  const [thinkOpen, setThinkOpen] = useState(false);
  const skills = useStore((s) => s.skills);
  const activePinnedSkillId = useStore((s) => s.activePinnedSkillId);
  const setActivePinnedSkill = useStore((s) => s.setActivePinnedSkill);
  const [skillOpen, setSkillOpen] = useState(false);

  const [optionsOpen, setOptionsOpen] = useState(false);
  const [inspectOpen, setInspectOpen] = useState(false);
  const activeId = useStore((s) => s.activeId);
  const [refreshing, setRefreshing] = useState(false);
  const [justSwitched, setJustSwitched] = useState(false);
  const prevModelRef = useRef(activeModel);
  const wasStreamingRef = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (prevModelRef.current && prevModelRef.current !== activeModel) {
      setJustSwitched(true);
    }
    prevModelRef.current = activeModel;
  }, [activeModel]);

  useEffect(() => {
    if (wasStreamingRef.current && !isStreaming) setJustSwitched(false);
    wasStreamingRef.current = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    if (!optionsOpen && !skillOpen && !thinkOpen) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOptionsOpen(false);
        setSkillOpen(false);
        setThinkOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOptionsOpen(false); setSkillOpen(false); setThinkOpen(false); }
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [optionsOpen, skillOpen, thinkOpen]);

  const handleOpenOptions = async () => {
    const next = !optionsOpen;
    setOptionsOpen(next);
    setSkillOpen(false);
    setThinkOpen(false);
    if (next) {
      setRefreshing(true);
      try { await refreshModels(); } finally { setRefreshing(false); }
    }
  };

  const activeInfo = models.find((m) => m.id === activeModel);

  return (
    <div ref={rootRef} className="flex w-full items-center gap-1 pb-1.5">
      <div className="relative">
        <button
          onClick={() => { setThinkOpen((v) => !v); setOptionsOpen(false); setSkillOpen(false); }}
          className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors ${
            thinkingEffort !== "off"
              ? "bg-primary/15 text-primary hover:bg-primary/20"
              : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
          title={`Thinking effort: ${thinkingEffort}`}
        >
          <i className="fa-solid fa-brain" />
          <span className="capitalize">{thinkingEffort === "off" ? "Think" : thinkingEffort}</span>
          <i className={`fa-solid fa-chevron-${thinkOpen ? "down" : "up"} text-[9px] opacity-60`} />
        </button>
        {thinkOpen && (
          <div className="absolute bottom-full left-0 z-50 mb-1 w-44 rounded-md border bg-popover py-1 text-sm shadow-md">
            {(["off", "low", "medium", "high"] as const).map((level) => (
              <button
                key={level}
                onClick={() => { void updateSettings({ thinkingEffort: level }); setThinkOpen(false); }}
                className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-muted ${level === thinkingEffort ? "text-primary" : ""}`}
              >
                <span className="capitalize">{level}</span>
                {level === thinkingEffort && <i className="fa-solid fa-check text-[10px]" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {skills.length > 0 && (() => {
        const pinned = skills.find((s) => s.id === activePinnedSkillId);
        return (
          <div className="relative ml-1">
            <button
              onClick={() => { setSkillOpen((v) => !v); setOptionsOpen(false); setThinkOpen(false); }}
              className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors ${
                pinned
                  ? "bg-primary/15 text-primary hover:bg-primary/20"
                  : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
              title={pinned ? `Skill: ${pinned.name}` : "Pin a skill to this chat"}
            >
              <i className="fa-solid fa-wand-magic-sparkles" />
              <span className="max-w-[10em] truncate">{pinned ? pinned.name : "Skill"}</span>
              <i className={`fa-solid fa-chevron-${skillOpen ? "down" : "up"} text-[9px] opacity-60`} />
            </button>
            {skillOpen && (
              <div className="absolute bottom-full left-0 z-50 mb-1 w-64 max-h-72 overflow-y-auto rounded-md border bg-popover py-1 text-sm shadow-md">
                <button
                  onClick={() => { void setActivePinnedSkill(null); setSkillOpen(false); }}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted ${!pinned ? "text-primary" : "text-muted-foreground"}`}
                >
                  <span>None</span>
                </button>
                {skills.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { void setActivePinnedSkill(s.id); setSkillOpen(false); }}
                    className={`flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left hover:bg-muted ${s.id === activePinnedSkillId ? "text-primary" : ""}`}
                  >
                    <span className="text-xs">{s.name}</span>
                    {s.description && (
                      <span className="line-clamp-2 text-[10px] text-muted-foreground">{s.description}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {justSwitched && (
        <span className="ml-2 hidden sm:inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          {isStreaming ? (
            <i className="fa-solid fa-spinner-third fa-spin text-[10px]" />
          ) : (
            <i className="fa-solid fa-clock text-[10px]" />
          )}
          <span>{isStreaming ? "Loading model…" : "First reply may take a moment"}</span>
        </span>
      )}

      <div className="ml-auto flex items-center gap-1">
        <button
          onClick={() => setInspectOpen(true)}
          disabled={!activeId}
          className="flex size-7 items-center justify-center rounded-md bg-muted/60 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-40"
          title="Inspect last prompt"
        >
          <i className="fa-solid fa-file-magnifying-glass" />
        </button>
        <div className="relative">
          <button
            onClick={handleOpenOptions}
            className="flex size-7 items-center justify-center rounded-md bg-muted/60 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title="Model & request options"
          >
            <i className="fa-solid fa-ellipsis" />
          </button>
          {optionsOpen && (
            <div className="absolute bottom-full right-0 z-50 mb-1 w-[min(90vw,28rem)] max-h-[60vh] overflow-y-auto rounded-md border bg-popover p-4 text-sm shadow-md space-y-4">
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <i className="fa-solid fa-microchip" />
                    <span>Model</span>
                  </div>
                  {refreshing && <i className="fa-solid fa-spinner-third fa-spin text-[10px] text-muted-foreground" />}
                </div>
                <div className="rounded-md border">
                  {models.length === 0 && (
                    <p className="px-3 py-2 text-xs text-muted-foreground">No models loaded.</p>
                  )}
                  {models.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { setActiveModel(m.id); }}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-muted ${m.id === activeModel ? "text-primary" : ""}`}
                    >
                      <span className="truncate font-mono text-xs">{m.id}</span>
                      <span className="shrink-0 flex items-center gap-2">
                        {m.contextLength ? (
                          <span className="text-[10px] text-muted-foreground">{formatCtx(m.contextLength)} ctx</span>
                        ) : null}
                        {m.id === activeModel && <i className="fa-solid fa-check text-[10px]" />}
                      </span>
                    </button>
                  ))}
                </div>
                {activeInfo && (
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    Active: <span className="font-mono">{activeModel}</span>
                    {activeInfo.contextLength ? ` · ${formatCtx(activeInfo.contextLength)} ctx` : ""}
                  </p>
                )}
              </div>
              <div className="border-t pt-3">
                <ContextPanelBody />
              </div>
            </div>
          )}
        </div>
      </div>
      {inspectOpen && <PromptInspector chatId={activeId} onClose={() => setInspectOpen(false)} />}
    </div>
  );
}
